import { useState } from 'react';
import PropTypes from 'prop-types';
import { uploadProfileImage, uploadBannerImage } from '../services/imageService';
import ImageCropper from './ImageCropper';

// Image size recommendations
const IMAGE_SPECS = {
  profile: {
    recommended: { width: 400, height: 400 },
    aspect: 1, // 1:1 square
    maxSize: 5 * 1024 * 1024, // 5MB
    description: 'Square profile picture',
    examples: '400×400px (recommended)'
  },
  banner: {
    recommended: { width: 1200, height: 400 },
    aspect: 3, // 3:1 landscape
    maxSize: 10 * 1024 * 1024, // 10MB
    description: 'Wide banner image',
    examples: '1200×400px (recommended)'
  }
};

// Helper function to create cropped image
const getCroppedImg = (imageSrc, pixelCrop) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      }, 'image/jpeg', 0.85);
    };
    image.onerror = reject;
  });
};

const UserImageUploader = ({ 
  userId, 
  currentImages = { profile: null, banner: null },
  onImageUpdate = () => {},
  theme = 'light'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperData, setCropperData] = useState(null);

  // Debug log current images
  if (import.meta.env.DEV) {
    console.log('🖼️ UserImageUploader received currentImages:', currentImages);
  }

  // Check if image needs cropping based on dimensions and aspect ratio
  const checkImageNeedsCropping = (file, imageType) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        const spec = IMAGE_SPECS[imageType];
        const currentAspect = img.width / img.height;
        const targetAspect = spec.aspect;
        
        // If aspect ratio is significantly different or image is much larger, suggest cropping
        const aspectDiff = Math.abs(currentAspect - targetAspect) / targetAspect;
        const isOversized = img.width > spec.recommended.width * 2 || img.height > spec.recommended.height * 2;
        
        URL.revokeObjectURL(url);
        resolve({
          needsCropping: aspectDiff > 0.1 || isOversized,
          currentDimensions: { width: img.width, height: img.height },
          currentAspect: currentAspect.toFixed(2),
          targetAspect: targetAspect.toFixed(2)
        });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ needsCropping: false });
      };
      
      img.src = url;
    });
  };

  // Upload image (extracted from original logic)
  const uploadImage = async (file, imageType) => {
    setLoading(true);
    setError(null);
    
    if (import.meta.env.DEV) {
      console.log('✅ File validation passed, starting upload...');
    }
    
    try {
      let result;
      
      if (imageType === 'profile') {
        if (import.meta.env.DEV) {
          console.log('🔄 Uploading profile image via imageService...');
        }
        result = await uploadProfileImage(userId, file);
      } else {
        if (import.meta.env.DEV) {
          console.log('🔄 Uploading banner image via imageService...');
        }
        result = await uploadBannerImage(userId, file);
      }
      
      if (import.meta.env.DEV) {
        console.log('✅ Image upload successful:', result);
      }
      
      // The result should contain the image URL
      if (result && result.url) {
        const fieldName = imageType === 'profile' ? 'profileImage' : 'bannerImage';
        if (import.meta.env.DEV) {
          console.log('🔄 Calling onImageUpdate with URL:', { fieldName, url: result.url });
        }
        onImageUpdate(fieldName, result.url);
      } else {
        if (import.meta.env.DEV) {
          console.log('⚠️ Upload succeeded but no URL returned:', result);
        }
        setError(`Upload succeeded but no image URL returned`);
      }
      
    } catch (error) {
      console.error(`❌ Error uploading ${imageType} image:`, error);
      setError(`Failed to upload ${imageType} image: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle image upload using proper file upload service
  const handleImageUpload = async (file, imageType) => {
    if (!file) {
      if (import.meta.env.DEV) {
        console.log('❌ No file selected');
      }
      return;
    }
    
    if (import.meta.env.DEV) {
      console.log('📁 File selected:', { 
        name: file.name, 
        size: file.size, 
        type: file.type,
        imageType 
      });
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      const errorMsg = 'Please select a valid image file (JPEG, PNG, GIF, or WebP)';
      if (import.meta.env.DEV) {
        console.log('❌ Invalid file type:', file.type);
      }
      setError(errorMsg);
      return;
    }
    
    // Check file size
    const spec = IMAGE_SPECS[imageType];
    if (file.size > spec.maxSize) {
      const errorMsg = `Image file size must be less than ${Math.round(spec.maxSize / (1024 * 1024))}MB`;
      if (import.meta.env.DEV) {
        console.log('❌ File too large:', file.size);
      }
      setError(errorMsg);
      return;
    }

    // Check if image needs cropping
    const cropCheck = await checkImageNeedsCropping(file, imageType);
    if (import.meta.env.DEV) {
      console.log('🔍 Crop check result:', cropCheck);
    }

    if (cropCheck.needsCropping) {
      // Show cropper
      const imageUrl = URL.createObjectURL(file);
      setCropperData({
        imageUrl,
        originalFile: file,
        imageType,
        specs: spec,
        dimensions: cropCheck.currentDimensions
      });
      setShowCropper(true);
      return;
    }

    // If no cropping needed, proceed with upload
    await uploadImage(file, imageType);
  };

  // Handle cropped image
  const handleCropComplete = async (croppedArea) => {
    try {
      setLoading(true);
      if (import.meta.env.DEV) {
        console.log('✂️ Cropping image...', croppedArea);
      }
      
      const croppedBlob = await getCroppedImg(cropperData.imageUrl, croppedArea);
      
      // Create a new file from the cropped blob
      const croppedFile = new File(
        [croppedBlob], 
        `cropped_${cropperData.originalFile.name}`, 
        { type: 'image/jpeg' }
      );
      
      // Clean up
      URL.revokeObjectURL(cropperData.imageUrl);
      setShowCropper(false);
      setCropperData(null);
      
      // Upload the cropped image
      await uploadImage(croppedFile, cropperData.imageType);
    } catch (error) {
      console.error('❌ Error cropping image:', error);
      setError('Failed to crop image. Please try again.');
      setLoading(false);
    }
  };

  // Cancel cropping
  const handleCropCancel = () => {
    if (cropperData?.imageUrl) {
      URL.revokeObjectURL(cropperData.imageUrl);
    }
    setShowCropper(false);
    setCropperData(null);
  };

  // Handle file input change
  const handleFileChange = (e, imageType) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file, imageType);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  // Get file input accept string based on image type
  const getAcceptTypes = () => {
    return 'image/jpeg,image/png,image/gif,image/webp';
  };

  // Tooltip component for image recommendations
  const ImageTooltip = ({ imageType, children }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const spec = IMAGE_SPECS[imageType];

    return (
      <div className="relative inline-block">
        <div
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-help"
        >
          {children}
        </div>
        
        {/* Tooltip */}
        {showTooltip && (
          <div className={`absolute z-50 px-3 py-2 text-xs rounded-lg shadow-lg border max-w-xs ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-600 text-gray-200' 
              : 'bg-white border-gray-200 text-gray-700'
          } ${imageType === 'profile' ? 'left-0 top-full mt-2' : 'right-0 top-full mt-2'}`}>
            <div className="space-y-1">
              <div className="font-medium text-blue-600 dark:text-blue-400">{spec.description}</div>
              <div>📐 Recommended: {spec.examples}</div>
              <div>📦 Max size: {Math.round(spec.maxSize / (1024 * 1024))}MB</div>
              <div>🎨 Supports: JPEG, PNG, GIF, WebP</div>
            </div>
            {/* Arrow */}
            <div className={`absolute w-2 h-2 transform rotate-45 ${
              theme === 'dark' ? 'bg-gray-800 border-l border-t border-gray-600' : 'bg-white border-l border-t border-gray-200'
            } ${imageType === 'profile' ? 'left-4 -top-1' : 'right-4 -top-1'}`}></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className={`p-3 rounded-lg border ${theme === 'dark' ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-200 text-red-700'}`}>
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {/* Banner Image Section */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Banner Image
          </label>
          <ImageTooltip imageType="banner">
            <svg className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </ImageTooltip>
        </div>
        <div
          className={`relative h-48 md:h-64 overflow-hidden cursor-pointer group ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} border-2 border-dashed rounded-lg transition-colors hover:border-blue-400`}
          onClick={() => !loading && document.getElementById('banner-upload').click()}
        >
          {currentImages.banner ? (
            <img src={currentImages.banner} alt="Banner" className="w-full h-full object-cover" />
          ) : (
            <div className={`w-full h-full ${theme === 'dark' ? 'bg-gradient-to-r from-[#2980b9] to-[#16a085]' : 'bg-gradient-to-r from-purple-400 to-pink-400'}`}></div>
          )}
          <div className={`absolute inset-0 ${currentImages.banner ? 'bg-black/20' : 'bg-black/20'}`}></div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="text-center text-white">
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  <span>Uploading...</span>
                </div>
              ) : (
                <>
                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-white font-medium">{currentImages.banner ? 'Change Banner' : 'Upload Banner'}</p>
                </>
              )}
            </div>
          </div>
        </div>
        <input
          id="banner-upload"
          type="file"
          accept={getAcceptTypes()}
          onChange={(e) => handleFileChange(e, 'banner')}
          className="hidden"
          disabled={loading}
        />
      </div>

      {/* Profile Image Section */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Profile Image
          </label>
          <ImageTooltip imageType="profile">
            <svg className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </ImageTooltip>
        </div>
        <div className="flex items-center space-x-4">
          <div
            className={`relative w-24 h-24 overflow-hidden cursor-pointer group rounded-full ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} border-2 border-dashed transition-colors hover:border-blue-400`}
            onClick={() => !loading && document.getElementById('profile-upload').click()}
          >
            {currentImages.profile ? (
              <img src={currentImages.profile} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className={`w-full h-full ${theme === 'dark' ? 'bg-gradient-to-r from-[#2980b9] to-[#16a085]' : 'bg-gradient-to-r from-purple-400 to-pink-400'}`}></div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              {loading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              ) : (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </div>
          </div>
          <div className="flex-1">
            <input
              id="profile-upload"
              type="file"
              accept={getAcceptTypes()}
              onChange={(e) => handleFileChange(e, 'profile')}
              className="hidden"
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* Image Cropper Modal */}
      {showCropper && cropperData && (
        <ImageCropper
          image={cropperData.imageUrl}
          aspect={cropperData.specs.aspect}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          cropShape={cropperData.imageType === 'profile' ? 'round' : 'rect'}
        />
      )}
    </div>
  );
};

UserImageUploader.propTypes = {
  userId: PropTypes.string.isRequired,
  currentImages: PropTypes.shape({
    profile: PropTypes.string,
    banner: PropTypes.string
  }),
  onImageUpdate: PropTypes.func,
  theme: PropTypes.string
};

export default UserImageUploader;
